import React, { useState, useEffect, useCallback } from 'react';
import { donations as donationsApi, collections as collectionsApi, offeringList } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification, useOptionList, useChurchConfig, useOpenRecordFromLink, fetchRecord, RECORD_GONE } from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { formatDate, formatCurrencyPrecise } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, DataTable, Pagination, Notification, PromptDialog, ConfirmDialog, FormLayout, Input, SearchableSelect, Badge, Button, Modal, RowActions, DateRangeFilter, PrintButton
} from '../../components/ui';
import OfferingSummary from './OfferingSummary';
import type { Column } from '../../components/ui';
import type { Donation, Collection } from '../../types';
import { getStoredUser, isAdmin } from '../../utils/auth';
import { requestDeletion, approveDeletion, keepRecord, isDeleteRequested } from '../../utils/records';

type OKind = 'DONATION' | 'COLLECTION';

/** A row from /api/offerings/page — a donation or collection in one shape. */
interface OfferingRow {
  id: number; type: OKind; receipt: string; date?: string; amount?: number;
  donorName?: string; purpose?: string;
  source?: string; givenBy?: string; countedBy?: string; verifiedBy?: string;
  status?: string; deleteReason?: string; deletedAt?: string;
  deleteRequestedBy?: string; deleteRequestedAt?: string; deleteApprovedBy?: string;
  createdBy?: string;
}

type OfferingForm = {
  type: OKind;
  date: string; amount: string;
  donorName: string; purpose: string;
  source: string; givenBy: string; countedBy: string; verifiedBy: string;
};
const EMPTY_FORM: OfferingForm = {
  type: 'DONATION', date: '', amount: '',
  donorName: '', purpose: '', source: '', givenBy: '', countedBy: '', verifiedBy: ''
};

const LIST_TABS = ['all', 'donation', 'collection', 'requested', 'deleted'];
const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const today = () => new Date().toISOString().slice(0, 10);

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const OfferingManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  // The Summary opens first; the register is one click away.
  const [activeTab, setActiveTab] = useState('summary');
  const [formData, setFormData] = useState<OfferingForm>(EMPTY_FORM);
  const [editingRow, setEditingRow] = useState<OfferingRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [receiptPreview, setReceiptPreview] = useState<OfferingRow | null>(null);
  /** True while the print dialog is being prepared, so the Actions column goes. */
  const [printing, setPrinting] = useState(false);
  const [pendingVoid, setPendingVoid] = useState<OfferingRow | null>(null);
  /** A deletion request an administrator is about to approve. */
  const [pendingApprove, setPendingApprove] = useState<OfferingRow | null>(null);
  // Deleting is two steps: anyone may ask, only an administrator decides.
  const canApproveDelete = isAdmin(getStoredUser());
  const userName = (getStoredUser()?.name || '').trim() || 'Unknown user';

  const { notification, notify, dismiss } = useNotification();
  const config = useChurchConfig();
  const personnel = useOptionList('church_personnel_list');

  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);
  // From / to, applied on the server so the page and the totals agree.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const clearRange = () => { setFromDate(''); setToDate(''); };

  const isDeletedView = activeTab === 'deleted';

  /** The filters the printout covers, for the masthead line. */
  const printReference = [
    activeTab === 'donation' ? 'Donations' : activeTab === 'collection' ? 'Collections' : 'All offerings',
    fromDate || toDate ? `${fromDate ? formatDate(fromDate) : 'start'} to ${toDate ? formatDate(toDate) : 'today'}` : null
  ].filter(Boolean).join('  •  ');
  const isRequestedView = activeTab === 'requested';
  const isListView = LIST_TABS.includes(activeTab);
  const typeParam = activeTab === 'donation' ? 'DONATION' : activeTab === 'collection' ? 'COLLECTION' : '';
  const bucketParam = isDeletedView ? 'deleted' : isRequestedView ? 'requested' : 'active';

  useEffect(() => { setPageNum(1); }, [activeTab, debouncedSearch, fromDate, toDate]);

  // The list is server-paged across both tables (the backend does a UNION), so
  // the browser never downloads every donation and collection.
  const { data: rows, raw: pageRaw, loading, refetch: refetchPage } =
    useFetchList<OfferingRow>((p, o) => offeringList.page(p, o) as Promise<OfferingRow[]>, {
      params: { type: typeParam, bucket: bucketParam, q: debouncedSearch, from: fromDate, to: toDate, page: pageNum - 1, size: PAGE_SIZE },
      enabled: isListView
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const [countsData, setCountsData] = useState({ all: 0, donation: 0, collection: 0, deleted: 0, requested: 0, sumDonation: 0, sumCollection: 0 });
  const refetchCounts = useCallback(async () => {
    try {
      const c = await offeringList.counts();
      setCountsData({
        all: Number(c.all) || 0, donation: Number(c.donation) || 0,
        collection: Number(c.collection) || 0, deleted: Number(c.deleted) || 0, requested: Number(c.requested) || 0,
        sumDonation: Number(c.sumDonation) || 0, sumCollection: Number(c.sumCollection) || 0
      });
    } catch { /* keep last counts */ }
  }, []);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts()]);
  }, [refetchPage, refetchCounts]);

  // The Summary needs every live row, not one page: the plain donations and
  // collections lists return them all, so they are read (again) each time the
  // tab opens and the figures follow any edit made on the other tabs.
  const [allDonations, setAllDonations] = useState<Donation[]>([]);
  const [allCollections, setAllCollections] = useState<Collection[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  useEffect(() => {
    if (activeTab !== 'summary') return;
    let live = true;
    setSummaryLoading(true);
    Promise.all([donationsApi.list(), collectionsApi.list()])
      .then(([d, c]) => { if (live) { setAllDonations(getList<Donation>(d)); setAllCollections(getList<Collection>(c)); } })
      .catch(() => { /* keep the last figures */ })
      .finally(() => { if (live) setSummaryLoading(false); });
    return () => { live = false; };
  }, [activeTab]);

  const page = {
    currentItems: rows, currentPage: pageNum, totalPages, totalItems, itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1, isLast: pageNum >= totalPages, reset: () => setPageNum(1)
  };

  const resetForm = () => { setFormData(EMPTY_FORM); setEditingRow(null); setErrors({}); };

  const switchTab = (key) => {
    if (key !== 'add') resetForm();
    setActiveTab(key);
    setSearchTerm('');
    setPageNum(1);
  };

  // "Open the record" from the Approvals inbox: a donation or a collection
  // (the request's receipt number says which), on its tab, in its receipt view.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const found = await fetchRecord(id, ref, { DONATION: donationsApi.get, COLLECTION: collectionsApi.get });
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    const r = found.record;
    const row: OfferingRow = {
      ...r, id: r.id, type: found.kind,
      receipt: found.kind === 'DONATION' ? (r.receiptNo || `REC-${r.id}`) : (r.receiptId || `#${r.id}`),
      amount: num(r.amount)
    };
    switchTab(r.status === 'VOID' ? 'deleted' : isDeleteRequested(r) ? 'requested' : found.kind.toLowerCase());
    setReceiptPreview(row);
  });

  const startEdit = (row: OfferingRow) => {
    setEditingRow(row);
    setFormData({
      type: row.type,
      date: row.date || '', amount: row.amount != null ? String(row.amount) : '',
      donorName: row.donorName || '', purpose: row.purpose || '',
      source: row.source || '', givenBy: row.givenBy || '',
      countedBy: row.countedBy || '', verifiedBy: row.verifiedBy || ''
    });
    setErrors({});
    setActiveTab('add');
  };

  const setField = (name: keyof OfferingForm) => (e) =>
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));

  // Rebuild the full entity payload from a row, so a void/restore full-replace
  // does not blank the columns the list did not carry.
  // An update replaces the whole row, so everything the list row carries goes
  // back — including who created it and the deletion trail.
  const audit = (r: OfferingRow) => ({
    status: (r.status || 'ACTIVE') as Donation['status'], deleteReason: r.deleteReason, deletedAt: r.deletedAt,
    deleteRequestedBy: r.deleteRequestedBy, deleteRequestedAt: r.deleteRequestedAt, deleteApprovedBy: r.deleteApprovedBy,
    createdBy: r.createdBy
  });
  const toDonation = (r: OfferingRow): Partial<Donation> =>
    ({ receiptNo: r.receipt, date: r.date, donorName: r.donorName, purpose: r.purpose, amount: r.amount, ...audit(r) });
  const toCollection = (r: OfferingRow): Partial<Collection> =>
    ({ receiptId: r.receipt, date: r.date, source: r.source, givenBy: r.givenBy, countedBy: r.countedBy, verifiedBy: r.verifiedBy, amount: r.amount, ...audit(r) });
  const putRow = (row: OfferingRow, change: Partial<Donation> & Partial<Collection>) => (row.type === 'DONATION'
    ? donationsApi.update(row.id, { ...toDonation(row), ...change })
    : collectionsApi.update(row.id, { ...toCollection(row), ...change }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isDonation = formData.type === 'DONATION';

    const found: Record<string, string> = {};
    if (!formData.date) found.date = 'The date is required.';
    else if (formData.date > today()) found.date = 'The date cannot be in the future.';
    if (num(formData.amount) <= 0) found.amount = 'Enter the amount.';
    if (isDonation) {
      if (!formData.donorName.trim()) found.donorName = 'Enter the donor name (or "Anonymous").';
      if (!formData.purpose.trim()) found.purpose = 'Choose the donation purpose.';
    } else {
      if (!formData.source.trim()) found.source = 'Choose the income source.';
      if (!formData.countedBy.trim()) found.countedBy = 'Record who counted the collection.';
    }
    if (Object.keys(found).length) {
      setErrors(found);
      notify('Please correct the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    const editing = Boolean(editingRow);
    setSaving(true);
    try {
      if (isDonation) {
        if (editing) {
          await donationsApi.update(editingRow!.id, {
            ...toDonation(editingRow!),
            date: formData.date, donorName: formData.donorName.trim(),
            purpose: formData.purpose.trim(), amount: formData.amount
          });
          await refetch(); resetForm(); setActiveTab('all');
          notify('Donation updated.', 'success');
        } else {
          // No receipt sent — the backend generates REC-<id> and returns it.
          const created = await donationsApi.create({
            date: formData.date, donorName: formData.donorName.trim(),
            purpose: formData.purpose.trim(), amount: formData.amount, status: 'ACTIVE'
          });
          await refetch(); resetForm(); setActiveTab('all');
          notify('Donation saved.', 'success');
          setReceiptPreview({
            id: created?.id as number, type: 'DONATION', receipt: created?.receiptNo || `REC-${created?.id}`,
            date: formData.date, amount: num(formData.amount),
            donorName: formData.donorName.trim(), purpose: formData.purpose.trim()
          });
        }
      } else {
        personnel.ensure(formData.countedBy.trim(), formData.verifiedBy.trim());
        const fields = {
          date: formData.date, source: formData.source.trim(), givenBy: formData.givenBy.trim(),
          countedBy: formData.countedBy.trim(), verifiedBy: formData.verifiedBy.trim(), amount: formData.amount
        };
        if (editing) {
          await collectionsApi.update(editingRow!.id, { ...toCollection(editingRow!), ...fields });
        } else {
          await collectionsApi.create({ ...fields, status: 'ACTIVE' });
        }
        await refetch(); resetForm(); setActiveTab('all');
        notify(editing ? 'Collection updated.' : 'Collection saved.', 'success');
      }
    } catch (err) {
      notify((err as Error)?.message || 'Could not save the offering.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Step 1: file the request. The offering stays in the list and in every total until it is decided. */
  const confirmVoid = async (reason: string) => {
    const row = pendingVoid;
    setPendingVoid(null);
    if (!row) return false;
    try {
      await putRow(row, requestDeletion(reason, userName));
      await refetch();
      notify(`Deletion of ${row.receipt} requested. An administrator has to approve it before it moves to Deleted History.`, 'success');
      return true;
    } catch (err) {
      notify((err as Error)?.message || 'Could not request the deletion.', 'error');
      return false;
    }
  };

  /** Step 2a: an administrator approves — only now is the offering voided. */
  const confirmApproveDelete = async () => {
    const row = pendingApprove;
    setPendingApprove(null);
    if (!row) return;
    try {
      await putRow(row, approveDeletion(row, userName));
      await refetch();
      notify(`${row.receipt} deleted. Kept under Deleted History.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not delete the offering.', 'error');
    }
  };

  /** Step 2b: an administrator keeps it, or restores a voided one — back to normal. */
  const handleRestore = async (row: OfferingRow) => {
    const wasRequest = isDeleteRequested(row);
    try {
      await putRow(row, keepRecord());
      await refetch();
      notify(wasRequest ? `${row.receipt} kept; the deletion request is closed.` : `${row.receipt} restored.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not restore the offering.', 'error');
    }
  };


  const typeBadge = (r: OfferingRow) => (r.type === 'DONATION'
    ? <Badge>🎁 Donation</Badge>
    : <Badge tone="warning">🪙 Collection</Badge>);

  const baseColumns: Array<Column<OfferingRow>> = [
    { key: 'receipt', header: 'Receipt', nowrap: true, render: (r) => <strong>{r.receipt}</strong> },
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'type', header: 'Type', nowrap: true, render: typeBadge },
    { key: 'giver', header: 'Giver / Source', render: (r) => (
      <span>
        {r.type === 'DONATION' ? (r.donorName || '-') : (r.source || r.givenBy || '-')}
        {/* Flagged in the live lists too, so nobody edits a row that is on its way out. */}
        {!isDeletedView && !isRequestedView && isDeleteRequested(r) && (
          <span style={{ display: 'block', marginTop: '3px' }}><Badge tone="warning">Deletion requested</Badge></span>
        )}
      </span>
    ) },
    { key: 'purpose', header: 'Purpose', render: (r) => (r.type === 'DONATION' ? (r.purpose ? <Badge tone="neutral">{r.purpose}</Badge> : '-') : '—') },
    {
      key: 'amount', header: 'Amount', align: 'right', nowrap: true,
      render: (r) => (
        <span style={{ fontWeight: 'bold', color: isDeletedView ? t.textFaint : t.income }}>
          {formatCurrencyPrecise(r.amount)}
        </span>
      )
    },
    { key: 'createdBy', header: 'Created By', render: (r) => r.createdBy || '-' }
  ];

  /** What a row offers depends on where it is in the delete flow, and on who is looking. */
  const rowMenu = (r: OfferingRow) => {
    if (isDeletedView) return [{ icon: '♻️', label: 'Restore', write: true, onClick: () => handleRestore(r) }];
    if (isDeleteRequested(r)) {
      return [
        { icon: '🧾', label: 'View / Receipt', onClick: () => setReceiptPreview(r) },
        ...(canApproveDelete
          ? [
              { icon: '✅', label: 'Approve delete', separated: true, danger: true, write: true, onClick: () => setPendingApprove(r) },
              { icon: '↩️', label: 'Keep record', write: true, onClick: () => handleRestore(r) }
            ]
          : [])
      ];
    }
    return [
      { icon: '🧾', label: 'View / Receipt', onClick: () => setReceiptPreview(r) },
      { icon: '✏️', label: 'Edit', write: true, onClick: () => startEdit(r) },
      { icon: '🗑️', label: 'Request delete', separated: true, danger: true, write: true, onClick: () => setPendingVoid(r) }
    ];
  };

  const actionsColumn: Column<OfferingRow> = {
    key: 'actions', header: 'Actions', align: 'right', nowrap: true,
    render: (r) => (
      <span onClick={(e) => e.stopPropagation()}>
        <RowActions isDarkMode={isDarkMode} items={rowMenu(r)} />
      </span>
    )
  };

  const requestColumns: Array<Column<OfferingRow>> = [
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
    { key: 'deleteRequestedBy', header: 'Requested By', render: (r) => (
      <span style={{ fontSize: '12px' }}>{r.deleteRequestedBy || '-'}<br /><span style={{ color: t.textFaint }}>{r.deleteRequestedAt || ''}</span></span>
    ) }
  ];

  const columns: Array<Column<OfferingRow>> = isDeletedView
    ? [
        ...baseColumns,
        ...requestColumns,
        { key: 'deletedAt', header: 'Deleted On', render: (r) => (
          <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}{r.deleteApprovedBy && <><br />by {r.deleteApprovedBy}</>}</span>
        ) },
        actionsColumn
      ]
    : isRequestedView
      ? [...baseColumns, ...requestColumns, actionsColumn]
      : [...baseColumns, actionsColumn];

  const isDonationForm = formData.type === 'DONATION';

  const typeToggle = (
    <div style={{ display: 'flex', border: `1px solid ${t.primary}`, borderRadius: t.radius.md, overflow: 'hidden', width: 'fit-content', marginBottom: t.spacing.lg }}>
      {(['DONATION', 'COLLECTION'] as OKind[]).map((k) => {
        const on = formData.type === k;
        return (
          <button
            key={k} type="button"
            onClick={() => setFormData((prev) => ({ ...prev, type: k }))}
            disabled={Boolean(editingRow)}
            style={{
              padding: '10px 22px', border: 'none', cursor: editingRow ? 'not-allowed' : 'pointer',
              fontWeight: 'bold', fontSize: t.fontSize.md,
              backgroundColor: on ? t.primary : t.bgRaised,
              color: on ? '#FFF' : t.primaryText, opacity: editingRow && !on ? 0.5 : 1
            }}
          >
            {k === 'DONATION' ? '🎁 Donation' : '🪙 Collection'}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="💰"
        title="Offerings & Collections"
        activeTab={activeTab}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'all', label: `All (${countsData.all})` },
          { key: 'donation', label: `🎁 Donations (${countsData.donation})` },
          { key: 'collection', label: `🪙 Collections (${countsData.collection})` },
          { key: 'requested', label: `⏳ Pending Deletion (${countsData.requested})`, variant: countsData.requested ? 'danger' : undefined },
          { key: 'deleted', label: `🗑️ Deleted History (${countsData.deleted})`, variant: 'danger' },
          { key: 'add', write: true, label: editingRow ? '✏️ Editing…' : '+ Add New Entry' }
        ]}
      />

      {isRequestedView && (
        <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: `0 0 ${t.spacing.md}` }}>
          {canApproveDelete
            ? 'These offerings are still counted in every total. Approve to move one to Deleted History, or keep it to cancel the request.'
            : 'These offerings are waiting for an administrator to approve their deletion. They still count in every total until then.'}
        </p>
      )}

      {isListView && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={() => setSearchTerm('')}
            placeholder="Search receipt, donor, source or purpose…"
            actions={
              <>
                <DateRangeFilter
                  startDate={fromDate} setStartDate={setFromDate} endDate={toDate} setEndDate={setToDate}
                  clear={clearRange} isActive={Boolean(fromDate || toDate)} isDarkMode={isDarkMode}
                />
              </>
            }
          />

          {/* Printed through the shared surface, so the register carries the
              parish's template instead of a screenshot of the app. */}
          <div>
            <DataTable
              columns={printing ? columns.filter((c) => c.key !== 'actions') : columns}
              rows={page.currentItems}
              loading={loading}
              rowKey={(r) => `${r.type}-${r.id}`}
              onRowClick={(r) => (isDeletedView ? undefined : setReceiptPreview(r))}
              emptyMessage="No offerings found."
            />
          </div>

          <Pagination {...page} itemsOnPage={page.currentItems.length} label="offerings" />
        </>
      )}

      {activeTab === 'summary' && (
        <OfferingSummary
          donations={allDonations}
          collections={allCollections}
          loading={summaryLoading}
          isDarkMode={isDarkMode}
        />
      )}

      {activeTab === 'add' && (
        <FormLayout
          title={editingRow ? `Edit ${isDonationForm ? 'Donation' : 'Collection'} — ${editingRow.receipt}` : 'Record New Offering'}
          submitLabel={editingRow ? 'Update' : (isDonationForm ? 'Save & Generate Receipt' : 'Save Collection')}
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={editingRow ? () => switchTab('all') : undefined}
          maxWidth="760px"
        >
          {typeToggle}

          <FormLayout.Grid>
            <Input label={isDonationForm ? 'Donation Date' : 'Collection Date'} name="date" type="date" required max={today()}
              value={formData.date} error={errors.date} onChange={setField('date')} />
            <Input label="Total Amount (₹)" name="amount" type="number" step="0.01" required
              placeholder="0.00" value={formData.amount} error={errors.amount} onChange={setField('amount')}
              style={{ fontSize: '18px', fontWeight: 'bold', color: t.income }} />
          </FormLayout.Grid>

          {isDonationForm ? (
            <FormLayout.Grid>
              <Input label='Donor Name (or "Anonymous")' name="donorName" required
                placeholder="e.g. John Doe" value={formData.donorName} error={errors.donorName} onChange={setField('donorName')} />
              <SearchableSelect
                label="Donation Type / Purpose" required
                value={formData.purpose}
                onChange={(v) => { setFormData((prev) => ({ ...prev, purpose: v })); setErrors((e) => ({ ...e, purpose: undefined })); }}
                storageKey="church_donation_purposes"  error={errors.purpose}
              />
            </FormLayout.Grid>
          ) : (
            <>
              <FormLayout.Grid>
                <SearchableSelect
                  label="Income Source" required
                  value={formData.source}
                  onChange={(v) => { setFormData((prev) => ({ ...prev, source: v })); setErrors((e) => ({ ...e, source: undefined })); }}
                  storageKey="church_income_sources"  error={errors.source}
                />
                <Input label="Offering Given By (Optional)" name="givenBy" placeholder="Name or Family"
                  value={formData.givenBy} onChange={setField('givenBy')} />
              </FormLayout.Grid>
              <FormLayout.Grid>
                <SearchableSelect
                  label="Counted By" required
                  value={formData.countedBy}
                  onChange={(v) => { setFormData((prev) => ({ ...prev, countedBy: v })); setErrors((e) => ({ ...e, countedBy: undefined })); }}
                  options={personnel.options} onOptionsChange={personnel.setOptions}  error={errors.countedBy}
                />
                <SearchableSelect
                  label="Verified By (Optional)"
                  value={formData.verifiedBy}
                  onChange={(v) => setFormData((prev) => ({ ...prev, verifiedBy: v }))}
                  options={personnel.options} onOptionsChange={personnel.setOptions}
                />
              </FormLayout.Grid>
            </>
          )}
        </FormLayout>
      )}

      {receiptPreview && (
        <Modal
          title={`Receipt ${receiptPreview.receipt}`}
          width="780px"
          onClose={() => setReceiptPreview(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <Button variant="secondary" onClick={() => setReceiptPreview(null)}>✕ Close</Button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant="ghost" onClick={() => { const r = receiptPreview; setReceiptPreview(null); startEdit(r); }}>✏️ Edit</Button>
                <PrintButton
                  module="Offerings"
                  documentType="OFFERING_RECEIPT"
                  title={`Receipt-${receiptPreview.receipt}`}
                  data={() => ({
                    ...receiptPreview,
                    date: formatDate(receiptPreview.date),
                    amount: formatCurrencyPrecise(receiptPreview.amount)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          }
        >
          <div style={{ color: t.textMuted }}>
            Receipt {receiptPreview.receipt} — {formatCurrencyPrecise(receiptPreview.amount)} from{' '}
            {receiptPreview.donorName || 'Anonymous'} on {formatDate(receiptPreview.date)}.
          </div>
        </Modal>
      )}

      <PromptDialog
        open={Boolean(pendingVoid)}
        title={`Request deletion of ${pendingVoid?.receipt || 'offering'}`}
        message="Give the reason. An administrator will approve or refuse the request; until then the offering stays in the register."
        placeholder="Reason for deletion"
        confirmLabel="Send request"
        requiredError="A reason is required to delete a financial record."
        onSubmit={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingApprove)}
        title={`Delete ${pendingApprove?.receipt || 'offering'}?`}
        message={pendingApprove
          ? `${formatCurrencyPrecise(pendingApprove.amount)} — ${pendingApprove.type === 'DONATION' ? pendingApprove.donorName : (pendingApprove.source || pendingApprove.givenBy)}. Requested by ${pendingApprove.deleteRequestedBy || 'unknown'}: "${pendingApprove.deleteReason || ''}". It moves to Deleted History and leaves every total.`
          : ''}
        confirmLabel="Approve delete"
        cancelLabel="Not now"
        onConfirm={confirmApproveDelete}
        onCancel={() => setPendingApprove(null)}
      />
    </div>
  );
};

export default OfferingManagement;
